<template>
    <aside class="doc-styles" aria-label="Estilos do documento">
        <header class="doc-styles__header">
            <span class="doc-styles__title">Estilos</span>
            <v-btn v-if="canManage" small text color="primary" @click="openForm(null)"><v-icon left small>mdi-plus</v-icon>Novo</v-btn>
            <v-btn icon x-small aria-label="Fechar estilos" @click="$emit('close')"><v-icon small>mdi-close</v-icon></v-btn>
        </header>

        <div class="doc-styles__body">
            <section v-for="group in groups" :key="group.type" class="doc-styles__group">
                <div class="doc-styles__group-title">
                    {{ group.title }}
                    <button v-if="group.type === 'character' && currentCharacterKey" type="button" class="doc-styles__link" @mousedown.prevent @click="clearCharacter">Remover do trecho</button>
                </div>
                <div v-for="style in group.styles" :key="style.key" class="doc-styles__item"
                    :class="{ 'doc-styles__item--current': isCurrent(style), 'doc-styles__item--inactive': style.active === false }">
                    <button type="button" class="doc-styles__apply" :disabled="style.active === false" :title="`Aplicar “${style.name}”`" @mousedown.prevent @click="apply(style)">
                        <span class="doc-styles__preview" :style="preview(style)">{{ style.name }}</span>
                        <span class="doc-styles__meta">{{ describe(style) }}</span>
                    </button>
                    <ui-action-menu v-if="canManage" icon-only small label="Ações do estilo" class="doc-styles__menu" :items="menuItems(style)" />
                </div>
            </section>
            <p v-if="error" class="doc-styles__error">{{ error }}</p>
            <p v-if="!state.remote" class="doc-styles__note">
                Estilos padrão do Guia de Formatação. {{ canManage ? "Os estilos criados ou alterados ficam disponíveis para todos quando o cadastro estiver ativo no servidor." : "" }}
            </p>
        </div>

        <editor-style-form v-model="form.open" :style-data="form.style" @saved="onSaved" />
    </aside>
</template>

<script>
import EditorStyleForm from "./EditorStyleForm.vue";
import { styleState, loadStyles, saveStyle, uniqueKey, previewCss, errorMessage } from "./styles/style-store";
import { currentParagraphStyleKey, currentCharacterStyleKey } from "./extensions/styles";
import { FONT_FAMILIES } from "./brand";

// Painel lateral de estilos do ui-document-editor: aplica estilos de
// paragrafo e de trecho e, para administradores, cria e edita os estilos
// (globais, valem para todos os documentos).
export default {
    name: "EditorStylesPanel",
    components: { EditorStyleForm },
    props: {
        editor: { type: Object, required: true },
        tick: { type: Number, default: 0 },
    },
    data: () => ({ state: styleState, form: { open: false, style: null }, error: null }),
    computed: {
        canManage() {
            return typeof this.$check === "function" && this.$check("DocumentStyle", "POST");
        },
        groups() {
            const visible = (type) => this.state.styles.filter((style) => style.type === type && (style.active !== false || this.canManage));
            return [
                { type: "paragraph", title: "Parágrafo", styles: visible("paragraph") },
                { type: "character", title: "Trecho", styles: visible("character") },
            ];
        },
        currentParagraphKey() {
            void this.tick;
            return currentParagraphStyleKey(this.editor);
        },
        currentCharacterKey() {
            void this.tick;
            return currentCharacterStyleKey(this.editor);
        },
    },
    created() {
        loadStyles();
    },
    methods: {
        isCurrent(style) {
            return style.type === "paragraph" ? style.key === this.currentParagraphKey : style.key === this.currentCharacterKey;
        },
        apply(style) {
            const chain = this.editor.chain().focus();
            if (style.type === "paragraph") chain.applyParagraphStyle(style).run();
            else chain.applyCharacterStyle(style).run();
        },
        clearCharacter() {
            this.editor.chain().focus().clearCharacterStyle().run();
        },
        preview(style) {
            const css = previewCss(style);
            // Tamanho limitado para a lista nao ficar desproporcional.
            const size = parseFloat(style.fontSize || "11");
            return { ...css, fontSize: `${Math.min(18, Math.max(12, size * 1.2))}px`, textAlign: "left" };
        },
        describe(style) {
            const font = FONT_FAMILIES.find((item) => item.value === (style.fontFamily || null));
            const parts = [font ? font.text.replace(" (padrão)", "") : "Segoe UI"];
            if (style.fontSize) parts.push(style.fontSize.replace("pt", " pt"));
            if (style.bold) parts.push("negrito");
            if (style.italic) parts.push("itálico");
            if (style.uppercase) parts.push("maiúsculas");
            if (style.active === false) parts.push("inativo");
            return parts.join(" · ");
        },
        menuItems(style) {
            return [
                { text: "Editar", icon: "mdi-pencil-outline", handler: () => this.openForm(style) },
                { text: "Duplicar", icon: "mdi-content-copy", handler: () => this.duplicate(style) },
                {
                    text: style.active === false ? "Ativar" : "Desativar",
                    icon: style.active === false ? "mdi-eye-outline" : "mdi-eye-off-outline",
                    disabled: style.system && style.key === "texto-normal",
                    handler: () => this.toggleActive(style),
                },
            ];
        },
        openForm(style) {
            this.form.style = style ? { ...style } : null;
            this.form.open = true;
        },
        duplicate(style) {
            const name = `${style.name} (cópia)`;
            const copy = { ...style, name, key: uniqueKey(name), system: false };
            delete copy.id;
            this.openForm(copy);
        },
        async toggleActive(style) {
            this.error = null;
            try {
                await saveStyle({ ...style, active: style.active === false });
            } catch (error) {
                this.error = errorMessage(error, "Não foi possível alterar o estilo. Tente novamente.");
            }
        },
        onSaved(style) {
            if (style && style.type === "paragraph") this.$nextTick(() => this.editor.commands.focus());
        },
    },
};
</script>
